import { t } from "@lingui/macro"
import { Alert, Button } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useEffect, useMemo, useState } from "react"

import { MixpanelProperties } from "src/analytics/constants/properties"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import { Form } from "src/components/Form"
import { ModalHeader } from "src/components/ModalHeader"
import { LibraryInformationItemsContentSection } from "src/modals/library-information-items/sections/Content"
import { LibraryInformationItemsPublishSettingsSection } from "src/modals/library-information-items/sections/PublishSettings"
import { LibraryInformationItemsSettingsSection } from "src/modals/library-information-items/sections/Settings"
import { LibraryInformationItemStore } from "src/modals/library-information-items/store"
import { ILibraryInformationItemModalProps } from "src/modals/library-information-items/types/libraryInformationItemProps"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { reportError } from "src/lib/report"
import { ILibraryInformationItem } from "src/types/library"

const Modal = observer((props: ILibraryInformationItemModalProps) => {
    const store = useStore(LibraryInformationItemStore)
    const globalStore = useStore(GlobalStore)
    const isCopyMode = props.mode === "Copy"

    const [thisModal] = useState(
        globalStore.modals.active[globalStore.modals.active.length - 1],
    )
    thisModal.confirmOnOverlayClick = () => store.formFields.getIsDirty()

    // Memoize the information item ID for settings section
    const informationItemId = useMemo(() => {
        if (isCopyMode) return 0
        return props.itemId ?? 0
    }, [isCopyMode, props.itemId])

    useEffect(() => {
        const initModal = async () => {
            const isSuperUser = globalStore.session.user?.isSuper === true
            // Track analytics
            if (props.itemId !== undefined) {
                trackModuleEvent(
                    `Library | ${isCopyMode ? "Copy" : "Detailed view"}`,
                    {
                        [MixpanelProperties.ItemID]: props.itemId,
                        [MixpanelProperties.AccessGroupName]:
                            globalStore.session.accessGroup?.name,
                        [MixpanelProperties.AccessGroupID]:
                            globalStore.session.accessGroup?.id,
                    },
                )
            }

            try {
                // Initialize with the ID
                await store.init(isSuperUser, props.itemId, isCopyMode)

                // For copy mode, append "(Copy)" to the name if we have an item
                if (isCopyMode && props.itemId !== undefined) {
                    // Get the admin name from the form fields after initialization
                    const adminName = store.formFields.get("internalName") ?? ""
                    if (adminName !== "") {
                        store.formFields.set(
                            "internalName",
                            `${adminName} (Copy)`,
                        )
                    }
                }
            } catch (error) {
                // Log initialization error
                reportError(t`global.load-data-fail"`, error)
            }
        }

        void initModal()
    }, [store, props.itemId, globalStore.session, isCopyMode])

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            store.setIsLoading(true)

            store.validateRequiredFields([
                "category",
                "internalName",
                "nameInApp",
                "type",
                "accessGroupId",
                store.formFields.get("type") ?? "",
            ])

            if (store.formFields.hasErrors() === true) {
                store.setIsLoading(false)
                return
            }
            trackModuleEvent("Library | Save", {
                [MixpanelProperties.ItemName]: store.formFields.data.nameInApp,
                [MixpanelProperties.ShowInApp]: store.formFields.data.showInApp,
                [MixpanelProperties.UsedByChatbot]:
                    store.formFields.data.includeInChatbot,
                [MixpanelProperties.AccessGroupName]:
                    globalStore.session.accessGroup?.name,
                [MixpanelProperties.AccessGroupID]:
                    globalStore.session.accessGroup?.id,
            })

            try {
                // Create a new item in copy mode or if no itemId was provided
                if (isCopyMode || props.itemId === undefined) {
                    await store.createItem()
                } else {
                    // Use the ID for update
                    const itemForUpdate = {
                        information_item_id: props.itemId,
                        id: props.itemId,
                        rank: 0,
                    } as ILibraryInformationItem
                    await store.updateItem(itemForUpdate)
                }

                if (!Boolean(store.formFields.error("generic"))) {
                    globalStore.modals.pop()
                }
            } catch (error) {
                reportError(
                    t`edit-information-item-modal.save-item-fail`,
                    error,
                )
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [globalStore.modals, props.itemId, store, isCopyMode],
    )

    const isButtonDisabled =
        store.formFields.get("accessType") === "READ" ||
        store.formFields.getIsDirty() === false ||
        store.isLoading

    return (
        <Form onSubmit={handleSubmit}>
            <>
                <ModalHeader>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isButtonDisabled}
                        data-testid="submit-item-form"
                    >
                        {t`global.save`}
                    </Button>
                </ModalHeader>
                {Boolean(store.formFields.error("generic")) && (
                    <Alert severity="error">
                        {store.formFields.error("generic")}
                    </Alert>
                )}
                <LibraryInformationItemsSettingsSection
                    id={informationItemId}
                />
                <LibraryInformationItemsContentSection />
                <LibraryInformationItemsPublishSettingsSection />
            </>
        </Form>
    )
})

export const LibraryInformationItemModal = (
    props: ILibraryInformationItemModalProps,
) => (
    <StoreProvider Store={LibraryInformationItemStore}>
        <Modal {...props} />
    </StoreProvider>
)
